import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { BlogPage } from "@/features/blog/components/blog-chrome";
import { useLoadInBrowser } from "@/features/blog/hooks/use-load-in-browser";
import { BlogIndex } from "@/features/blog/modules/blog-index/blog-index";
import { blogListRequest } from "@/features/blog/utils/blog-list-request";
import { BlogHero } from "@/features/blog/modules/blog-index/components/blog-hero";
import { blogCategoryLinks, blogListHead } from "@/features/blog/utils/blog-head";
import { reportApiOutage } from "@/lib/api-outage";
import { m } from "@/paraglide/messages.js";
import { getUrlOrigin } from "@/paraglide/runtime.js";

/**
 * One shelf of the blog: its name and description on top, its posts below. Each language has its
 * own address for it; one from another language (an old link, or the language switcher keeping the
 * path) moves for good to the reader's. An unknown shelf is a 404.
 */
export const Route = createFileRoute("/(public)/blog/category/$categorySlug")({
  staticData: { ownAlternates: true },
  loader: async ({ params }) => {
    const { data, status } = await blogListRequest({ category: params.categorySlug }).send();
    if (status === 404 || (data && !data.category)) throw notFound();
    if (data?.category && data.category.slug !== params.categorySlug) {
      throw redirect({
        to: "/blog/category/$categorySlug",
        params: { categorySlug: data.category.slug },
        statusCode: 301,
      });
    }
    // On the server a failed request hands the page to the browser, which asks again.
    if (!data) reportApiOutage();
    if (!data && typeof window !== "undefined")
      throw new Error(`Could not load this shelf (status ${status ?? "none"})`);
    return { index: data ?? null, crumb: data?.category?.name ?? m.blog_title() };
  },
  head: ({ loaderData }) => {
    const category = loaderData?.index?.category;
    if (!loaderData?.index || !category) return {};
    const head = blogListHead(loaderData.index, {
      origin: getUrlOrigin(),
      title: `${category.name} · ${m.blog_title()}`,
      description: category.description || m.blog_category_description({ name: category.name }),
      blogTitle: m.blog_title(),
      path: `/category/${category.slug}`,
    });
    return { ...head, links: [...head.links, ...blogCategoryLinks(category, getUrlOrigin())] };
  },
  component: BlogCategoryPage,
});

function BlogCategoryPage() {
  const { index } = Route.useLoaderData();
  const { categorySlug } = Route.useParams();
  useLoadInBrowser(index);
  const category = index?.category;

  return (
    <BlogPage hero={category ? <BlogHero title={category.name} lede={category.description || undefined} /> : null}>
      {index ? <BlogIndex index={index} filter={{ category: categorySlug }} /> : null}
    </BlogPage>
  );
}
